import "@/App.css";
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { flushSync } from "react-dom";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Catalog from "./pages/Catalog";
import Guides from "./pages/Guides";
import GuideDetail from "./pages/GuideDetail";
import Sectores from "./pages/Sectores";
import SectorDetail from "./pages/SectorDetail";
import ProductDetail from "./pages/ProductDetail";
import Tracking from "./pages/Tracking";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import { isUpdateReady } from "./serviceWorkerRegistration";
import { CURRENT_SEASON } from "./data/season";

/**
 * Rutas diferidas: cada una viaja en su propio archivo y solo se descarga
 * cuando alguien entra en ella.
 *
 * Qué se queda y qué se va. Se quedan en el paquete principal las rutas
 * PRE-RENDERIZADAS (ver scripts/prerender.js: portada, catálogo, guías,
 * sectores, fichas de producto, privacidad y rastreo) — son las que llegan
 * por búsqueda, las que un rastreador visita, y las que deben pintar sin
 * esperar una segunda descarga. Se van las que solo se alcanzan navegando
 * dentro del sitio con una intención concreta: carrito, pago, cuenta,
 * pedidos y el panel de admin.
 *
 * El caso claro es Admin: son 1199 líneas —la página más grande con
 * diferencia— que hasta ahora bajaba TODO visitante para que las usara una
 * sola persona.
 */
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutResult = lazy(() => import("./pages/CheckoutResult"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MiNegocio = lazy(() => import("./pages/MiNegocio"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

/**
 * Relleno mientras llega el archivo de una ruta diferida.
 *
 * Reserva alto de pantalla a propósito (min-h-[60vh]): sin eso, el footer
 * saltaría hasta arriba durante la fracción de segundo que tarda la
 * descarga y volvería a bajar al montar la página — justo el tipo de salto
 * que costó llevar el CLS a 0. No lleva spinner: en una conexión normal
 * esto dura menos de lo que tarda en percibirse, y un spinner parpadeante
 * se ve peor que un espacio en calma.
 */
function RouteFallback() {
    return <div className="min-h-[60vh]" aria-hidden="true" />;
}

/**
 * Cross-fade nativo entre páginas (View Transitions API) sin cambiar de
 * router: <Routes> sigue montado con `location`, pero se le pasa una copia
 * en estado (`displayLocation`) que solo se actualiza DENTRO del callback
 * de `document.startViewTransition`. `flushSync` fuerza ese cambio a pintar
 * de forma síncrona — sin él, React difiere el render y la API captura la
 * foto "después" antes de que el DOM nuevo exista. Sin soporte del
 * navegador (o con "reduce motion"), cae directo a actualizar el estado:
 * la navegación funciona igual, solo sin el cross-fade.
 */
// Marca la temporada vigente en <html> para que el CSS pueda reaccionar
// (hoy: color de selección en Halloween). Se apaga sola fuera de fechas.
if (typeof document !== 'undefined' && CURRENT_SEASON) {
    document.documentElement.dataset.season = CURRENT_SEASON.id;
}

function AppRoutes() {
    const location = useLocation();
    const navType = useNavigationType();
    const [displayLocation, setDisplayLocation] = useState(location);
    // Posición de scroll de cada entrada del historial, para poder volver a
    // ella con "atrás". Vive en un ref: no debe provocar renders.
    const scrollByKey = useRef(new Map());

    // El navegador restaura el scroll por su cuenta al volver atrás, pero lo
    // hace ANTES de que este componente cambie de página (el cambio va
    // diferido por la transición), así que restauraba sobre el contenido
    // viejo y la página nueva aparecía en una posición cualquiera. Se toma
    // el control a mano.
    useEffect(() => {
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    }, []);

    /**
     * Cambia a la ruta nueva Y coloca el scroll, en el mismo paso.
     *
     * Sin esto no había ningún manejo de scroll entre páginas: al abrir una
     * ficha desde el catálogo la ventana conservaba la posición de la lista
     * (cientos de píxeles abajo). Como la ficha es más corta, el navegador
     * la recortaba al fondo de la página y se veía el final en vez del
     * principio. Ahora: navegar hacia adelante sube al inicio (o al ancla si
     * el enlace trae #hash) y "atrás" recupera donde se estaba.
     */
    const show = (loc) => {
        setDisplayLocation(loc);
        return loc;
    };
    const placeScroll = (loc) => {
        if (loc.hash) {
            const el = document.getElementById(decodeURIComponent(loc.hash.slice(1)));
            if (el) { el.scrollIntoView(); return; }
        }
        const saved = navType === 'POP' ? scrollByKey.current.get(loc.key) : undefined;
        window.scrollTo(0, saved || 0);
        if (!saved) return;
        // Al volver, la página todavía está armando su altura (imágenes,
        // datos que llegan de la API): un scroll a 1900px sobre una página
        // que aún mide 1500 queda recortado. Se reintenta hasta que la
        // altura alcanza (máx. 3 s) y se corta al instante si la persona
        // toma el control con la rueda, el dedo o el teclado.
        const t0 = performance.now();
        let stop = false;
        const cancel = () => { stop = true; };
        const evs = ['wheel', 'touchstart', 'keydown', 'mousedown'];
        evs.forEach((e) => window.addEventListener(e, cancel, { once: true, passive: true }));
        const retry = () => {
            if (stop || Math.abs(window.scrollY - saved) < 2 || performance.now() - t0 > 3000) {
                evs.forEach((e) => window.removeEventListener(e, cancel));
                return;
            }
            window.scrollTo(0, saved);
            requestAnimationFrame(retry);
        };
        requestAnimationFrame(retry);
    };

    useEffect(() => {
        if (location.pathname === displayLocation.pathname) {
            // Misma página (cambia solo el query o el hash): no se salta.
            setDisplayLocation(location);
            return;
        }
        // Si mientras la app estaba abierta se desplegó una versión nueva,
        // esta navegación se hace como carga completa: es el momento seguro
        // para cambiar de JavaScript (la persona ya decidió irse de la
        // página), y así no se queda corriendo código viejo por días.
        if (isUpdateReady() && navType !== 'POP') {
            window.location.assign(location.pathname + location.search + location.hash);
            return;
        }
        // Se guarda dónde estaba la página que se va a dejar.
        scrollByKey.current.set(displayLocation.key, window.scrollY);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!document.startViewTransition || reduceMotion) {
            flushSync(() => show(location));
            placeScroll(location);
            return;
        }
        // El navegador puede abortar una transición en curso (el usuario
        // navega dos veces seguido muy rápido, o la pestaña pierde
        // visibilidad a medio cruce) — tanto `ready` como `finished`
        // rechazan en ese caso, cada uno por separado. Sin estos catches,
        // cada abort quedaba como DOS promesas sin manejar en la consola
        // aunque la navegación en sí (el cambio de página) termina bien
        // de todas formas: `flushSync` ya corrió, el aborto solo cancela
        // la animación visual del cruce, nunca la navegación.
        const transition = document.startViewTransition(() => {
            flushSync(() => show(location));
            placeScroll(location);
        });
        transition.ready.catch(() => {});
        transition.updateCallbackDone.catch(() => {});
        transition.finished.catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    return (
        // Suspense envuelve TODO el bloque de rutas, no cada ruta por
        // separado: así el relleno es uno solo y las rutas que no están
        // diferidas siguen montando de inmediato, sin pasar por él.
        <Suspense fallback={<RouteFallback />}>
        <Routes location={displayLocation}>
            <Route path="/" element={<Home />} />
            <Route path="/catalogo" element={<Catalog />} />
            <Route path="/guias" element={<Guides />} />
            <Route path="/guias/:slug" element={<GuideDetail />} />
            <Route path="/papel-para" element={<Sectores />} />
            <Route path="/papel-para/:slug" element={<SectorDetail />} />
            <Route path="/producto/:id" element={<ProductDetail />} />
            <Route path="/carrito" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/resultado" element={<CheckoutResult />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/recuperar" element={<ForgotPassword />} />
            <Route path="/restablecer" element={<ResetPassword />} />
            <Route path="/pedidos" element={<MiNegocio />} />
            <Route path="/pedido/:id" element={<OrderDetail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/privacidad" element={<Privacy />} />
            <Route path="/terminos" element={<Terms />} />
            {/* Rastreo público: la página existía pero nunca se enrutó. */}
            <Route path="/rastreo" element={<Tracking />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
    );
}

function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                <div className="min-h-screen" style={{ backgroundColor: 'var(--paper)' }}>
                    <Navbar />
                    <main>
                        <AppRoutes />
                    </main>
                    <Footer />
                </div>
                <Toaster position="top-right" />
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
